import { AfterContentInit, Component, ElementRef, Inject, forwardRef } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import * as moment_tz from 'moment-timezone';
import { take } from "rxjs";
import { ActionDefinition, ActionState, ActionStatus, ActionTopic, ActivationPeriod, ActivationType, Metric, PagedList, Thing, ThingActionOverride, WorkOrder } from "../../model";
import { AppService } from "../../service/app.service";
import { AuthenticationService } from "../../service/authentication.service";
import { ContextService } from "../../service/context.service";
import { ThingActionOverrideService } from "../../service/thing-action-override.service";
import { ThingContextService } from "../../service/thing-context.service";
import { UserActionStatusService } from "../../service/user-action-status.service";
import { CustomTableColumn, CustomTableService } from "../../shared/custom-table";
import { ListWidgetV2Component } from "../../shared/list-widget-v2/list-widget-v2.components";
import { LocalizationPipe } from "../../shared/pipe";
import { ErrorUtility } from "../../utility/error-utility";
import { ActionInfoDialogV2Component } from "../action-info-dialog/action-info-dialog-v2.component";
import { WidgetPrintResizeCoordinatorService } from "../shared/widget-print-resize-coordinator.service";
import { ActionListWidgetV2Service } from "./action-list-widget-v2.service";
import { CreateUncodedActionDialogComponent } from "./create-uncoded-action-dialog.component";

@Component({
    selector: 'add-action-dialog',
    template: require('./add-action-dialog.component.html'),
    providers: [ActionListWidgetV2Service]
})
export class AddActionDialogComponent extends ListWidgetV2Component<ActionDefinition> implements AfterContentInit {

    topics: ActionTopic[];
    displayedColumns: CustomTableColumn[];
    uncodedDialogOnly: boolean;

    private thing: Thing;
    private actionListWidgetV2Service: ActionListWidgetV2Service;
    private isActive: boolean;
    private firstSearch: boolean = true;
    private defaultDisplayedColumns: CustomTableColumn[] = [
        CustomTableService.newSimpleColumn('label', 'actionProperty', 'label').withSortField('label'),
        CustomTableService.newSimpleColumn('topic', 'actionDefinitionTopicProperty', 'topic').withSortField('topic'),
        CustomTableService.newSimpleColumn('type', 'actionDefinitionTypeProperty', 'type.label').withSortField('type.label'),
        CustomTableService.newSimpleColumn('workTime', 'workTimeInMinutesProperty', 'workTime').withSortField('workTime')
    ];
    private workOrder: WorkOrder;
    private timezone: string;
    private metrics: Metric[];
    private actionStatuses: ActionStatus[];
    private thingActionOverrides: ThingActionOverride[];

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<AddActionDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => AuthenticationService)) authenticationService: AuthenticationService,
        @Inject(forwardRef(() => AppService)) appService: AppService,
        @Inject(forwardRef(() => MatDialog)) dialog: MatDialog,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => ThingActionOverrideService)) private thingActionOverrideService: ThingActionOverrideService,
        @Inject(forwardRef(() => ThingContextService)) private thingContextService: ThingContextService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => ContextService)) private contextService: ContextService,
        @Inject(forwardRef(() => ElementRef)) elementRef: ElementRef,
        @Inject(forwardRef(() => WidgetPrintResizeCoordinatorService)) coordinator: WidgetPrintResizeCoordinatorService,
    ) {
        super(appService, authenticationService, null, null, dialog, null, null, elementRef, coordinator);
        this.thing = data.thing;
        this.isActive = data.isActive;
        this.actionListWidgetV2Service = data.actionListWidgetV2Service;
        this.workOrder = data.workOrder;
        this.displayedColumns = data.displayedColumns?.length ? data.displayedColumns : this.defaultDisplayedColumns;
        this.timezone = this.contextService.getTimezone();
    }

    ngAfterContentInit(): void {
        this.sort = ['label', 'asc'];
        const advancedSearchBody = {
            statuses: [ActionState.DONE, ActionState.DISCARDED, ActionState.TODO]
        }
        let promises = [];
        promises.push(this.actionListWidgetV2Service.getRecursivelyAllStatuses(null, null, advancedSearchBody));
        promises.push(this.thingContextService.getMetrics());
        promises.push(this.thingActionOverrideService.getByThingId(this.thing.id));
        Promise.all(promises).then(results => {
            this.actionStatuses = results[0];
            this.metrics = results[1];
            this.thingActionOverrides = results[2];
            if (this.isActive && this.actionStatuses.some(s => s.state != ActionState.TODO && [ActivationType.PERIOD, ActivationType.USAGE].includes(s.actionDefinition?.activationType))) {
                this.displayedColumns.push(CustomTableService.newSimpleColumn('recurrence', 'recurrenceProperty', 'recurrence'));
            }
            this.getActionDefinitionList();
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    getActionDefinitionList(): void {
        this.loaded = false;
        this.error = null;
        this.getRecursivelyAllActionDefinition().then(actionDefinitions => {
            if (this.isActive) {
                const user = this.authenticationService.getUser();
                actionDefinitions = actionDefinitions.filter(d => !this.actionStatuses.some(s => s.actionDefinitionId == d.id && s.state == ActionState.TODO) &&
                    (!d.userTypeFiltered || d.writeUserTypeIds?.includes(user.userTypeId)));
            }
            let pagedList: PagedList<ActionDefinition> = new PagedList<ActionDefinition>();
            pagedList.content = actionDefinitions;
            if (this.isActive) {
                this.enrichElementList(pagedList, this.actionStatuses, this.thingActionOverrides);
                this.updateElementList(pagedList);
                this.checkOpenCreateUncodedDialog(actionDefinitions);
            } else {
                this.updateElementList(pagedList);
                this.checkOpenCreateUncodedDialog(actionDefinitions);
            }
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    private enrichElementList(pagedList: PagedList<ActionDefinition>, allStatuses: ActionStatus[], thingActionOverrides: ThingActionOverride[]): void {
        pagedList.content.forEach(a => {
            this.handleThingActionOverrides(a, thingActionOverrides);
            if ([ActivationType.PERIOD, ActivationType.USAGE].includes(a.activationType)) {
                a['recurrence'] = this.getRecurrenceValue(allStatuses.find(s => s.actionDefinitionId == a.id));
            }
        });
    }

    private handleThingActionOverrides(actionDefinition: ActionDefinition, thingActionOverrides: ThingActionOverride[]): void {
        const overrides = thingActionOverrides.filter(o => o.actionDefinitionId == actionDefinition.id && !o.actionPropertyDefinitionId);
        overrides.forEach(override => {
            if (override.sparePartReferences) {
                actionDefinition.sparePartReferences = override.sparePartReferences;
            } else if (override.available == false) {
                actionDefinition['disabledTableRow'] = true;
            } else {    // worktime
                actionDefinition.workTime = override.workTime;
            }
        });
    }

    private checkOpenCreateUncodedDialog(actionDefinitions: ActionDefinition[]): void {
        if (this.firstSearch) {
            this.firstSearch = false;
            if (!actionDefinitions?.length) {
                this.uncodedDialogOnly = true;
                this.dialogRef.addPanelClass('d-none'); // hide panel
                this.openCreateUncodedDialog();
            }
        }
    }

    refreshList(data?: { pageIndex: number, pageSize: number, advancedSearchBody: any, sort: string[] }): void {
        this.loaded = false;
        if (data) {
            this.pageIndex = data.pageIndex;
            this.pageSize = data.pageSize;
            this.advancedSearchBody = data.advancedSearchBody;
            this.sort = data.sort;
        }
        this.getActionDefinitionList();
    }

    protected goToDetail(element: ActionDefinition): void {
        let body: ActionStatus = {} as ActionStatus;
        body.thingId = this.thing.id;
        body.actionDefinitionId = element.id;
        body.actionDefinition = element;
        body.state = ActionState.TODO;
        if (this.isActive && this.workOrder) {
            body.workOrderId = this.workOrder.id;
        }
        this.userActionStatusService.createActionStatus(body, !this.isActive).then(action => {
            this.dialogRef.close(action);
        }).catch(err => this.error = ErrorUtility.getMessage(err));
    }

    protected subscribeToExportServices(): void {
        // do nothing
    }

    openCreateUncodedDialog(): void {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.minWidth = '25%';
        dialogConfig.maxWidth = '428px';
        dialogConfig.panelClass = "create-uncoded-action-dialog";
        dialogConfig.autoFocus = false;
        dialogConfig.data = {
            thing: this.thing,
            isActive: this.isActive,
            workOrder: this.workOrder
        }
        this.dialog.open(CreateUncodedActionDialogComponent, dialogConfig).afterClosed().pipe(take(1)).subscribe(action => {
            if (action) {
                this.dialogRef.close(action);
            } else if (this.uncodedDialogOnly) {
                this.dialogRef.close();
            }
        });
    }

    private getRecurrenceValue(actionStatus: ActionStatus): string {
        if (actionStatus?.actionDefinition?.activationType == ActivationType.USAGE) {
            const metric = this.metrics.find(m => m.id == actionStatus.actionDefinition.usageMetricId);
            const recurrenceUnit = metric?.unit ? this.localizationPipe.transform(metric.unit) : null;
            const recurrenceTitle = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_TITLE).replace('${value}', actionStatus.actionDefinition.usageThreshold + (recurrenceUnit ? (' ' + recurrenceUnit) : ''));
            const recurrenceValue = actionStatus.usage != null ? actionStatus.usage : 0;;
            const recurrenceThreshold = actionStatus.actionDefinition.usageThreshold;
            const totalRemaining = recurrenceThreshold - recurrenceValue;
            const remainingInfo = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_REMAINING_INFO).replace('${value}', totalRemaining + (recurrenceUnit ? (' ' + recurrenceUnit) : ''));
            const recurrenceRemainingInfo = ' (' + remainingInfo + ')';
            return recurrenceTitle + recurrenceRemainingInfo;
        } else if (actionStatus?.actionDefinition?.activationType == ActivationType.PERIOD) {
            const period: ActivationPeriod = actionStatus.period || actionStatus.actionDefinition.period;
            const periodCount = actionStatus.periodCount != null ? actionStatus.periodCount : actionStatus.actionDefinition.periodCount;
            const dayOfWeek = actionStatus.dayOfWeek != null ? actionStatus.dayOfWeek : actionStatus.actionDefinition.dayOfWeek;
            const dayOfMonth = actionStatus.dayOfMonth != null ? actionStatus.dayOfMonth : actionStatus.actionDefinition.dayOfMonth;
            const now = moment_tz.tz(this.timezone);
            const actionTimestampDate = moment_tz.tz(actionStatus.timestamp, this.timezone);
            let periodUnit;
            let nextActivationDate;
            switch (period) {
                case ActivationPeriod.DAILY:
                    periodUnit = "day";
                    nextActivationDate = actionTimestampDate.add(periodCount, 'days').startOf('day');
                    break;
                case ActivationPeriod.WEEKLY:
                    periodUnit = "week";
                    nextActivationDate = actionTimestampDate.add(periodCount, 'weeks').startOf('day');
                    if (nextActivationDate.isoWeekday() > dayOfWeek) {
                        nextActivationDate = nextActivationDate.add(1, 'weeks');
                    }
                    nextActivationDate = nextActivationDate.isoWeekday(dayOfWeek);
                    break;
                case ActivationPeriod.MONTHLY:
                    periodUnit = "month";
                    nextActivationDate = actionTimestampDate.add(periodCount, 'months').startOf('day');
                    if (nextActivationDate.daysInMonth() < dayOfMonth || nextActivationDate.date() > dayOfMonth) {
                        nextActivationDate = nextActivationDate.add(1, 'months');
                    }
                    nextActivationDate = nextActivationDate.set('date', dayOfMonth);
                    break;
            }
            if (periodCount > 1) {
                periodUnit += 's';
            }
            const recurrenceTitle = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_TITLE).replace('${value}', periodCount + ' ' + this.localizationPipe.transform(periodUnit));
            const remainingDays = moment_tz.duration(nextActivationDate.valueOf() - now.valueOf(), 'milliseconds').asDays();
            const recurrenceRemainingInfo = this.localizationPipe.transform(ActionInfoDialogV2Component.RECURRENCE_REMAINING_INFO).replace('${value}', Math.ceil(remainingDays) + ' ' + this.localizationPipe.transform(remainingDays != 1 ? 'days' : 'day'));
            return recurrenceTitle + ' (' + recurrenceRemainingInfo + ')';
        }
        return null;
    }

    private getRecursivelyAllActionDefinition(page?: number, actionDefinitions?: ActionDefinition[]): Promise<ActionDefinition[]> {
        if (!page) {
            page = 0;
        }
        if (!actionDefinitions) {
            actionDefinitions = [];
        }
        return this.actionListWidgetV2Service.getActionDefinitionPagedList(page, 100, this.sort, this.advancedSearchBody, this.topics)
            .then(pagedMetrics => {
                actionDefinitions = actionDefinitions.concat(pagedMetrics.content);
                if (pagedMetrics.last) {
                    return actionDefinitions;
                } else {
                    return this.getRecursivelyAllActionDefinition(++page, actionDefinitions);
                }
            });
    }
}